import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { marked } from "marked";

import { api } from "../../lib/api";
import { RichEditor } from "./RichEditor";

const inputStyle: React.CSSProperties = {
  padding: "8px 12px", background: "var(--surface-input)", border: "1px solid var(--line)",
  borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none", width: "100%",
  boxSizing: "border-box",
};

export interface WikiDraft {
  title: string;
  body_md: string; // artigos novos guardam HTML; legados em markdown são convertidos ao abrir
  tags: string[];
}

export function WikiEditor({
  draft,
  onChange,
}: {
  draft: WikiDraft;
  onChange: (d: WikiDraft) => void;
}) {
  const [tagInput, setTagInput] = useState("");

  const { data: knownTags = [] } = useQuery<string[]>({
    queryKey: ["wiki-tags"],
    queryFn: () => api.get("/wiki/tags"),
  });

  // Conteúdo legado em markdown vira HTML uma única vez, na montagem do editor.
  const initialHtml = useMemo(() => {
    const body = draft.body_md;
    if (!body.trim() || body.trimStart().startsWith("<")) return body;
    return marked.parse(body, { async: false, gfm: true, breaks: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function addTag(raw: string) {
    const t = raw.trim();
    if (!t || draft.tags.includes(t)) { setTagInput(""); return; }
    onChange({ ...draft, tags: [...draft.tags, t] });
    setTagInput("");
  }

  const tagSuggestions = tagInput
    ? knownTags.filter(t => t.toLowerCase().includes(tagInput.toLowerCase()) && !draft.tags.includes(t)).slice(0, 6)
    : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <input
        value={draft.title}
        onChange={e => onChange({ ...draft, title: e.target.value })}
        placeholder="Título do artigo"
        maxLength={200}
        style={{ ...inputStyle, fontSize: 16, fontWeight: 600, padding: "10px 14px" }}
      />

      {/* Tags */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, position: "relative" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          {draft.tags.map(t => (
            <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, padding: "3px 10px", borderRadius: 99, background: "var(--brand-soft)", color: "var(--brand)", border: "1px solid var(--brand)" }}>
              {t}
              <button
                onClick={() => onChange({ ...draft, tags: draft.tags.filter(x => x !== t) })}
                style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", padding: 0, fontSize: 12, lineHeight: 1 }}
                aria-label={`Remover tag ${t}`}
              >
                ×
              </button>
            </span>
          ))}
          <input
            value={tagInput}
            onChange={e => setTagInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(tagInput); }
              if (e.key === "Backspace" && !tagInput && draft.tags.length) {
                onChange({ ...draft, tags: draft.tags.slice(0, -1) });
              }
            }}
            placeholder={draft.tags.length ? "" : "Tags (Enter para adicionar)"}
            style={{ ...inputStyle, width: 220 }}
          />
        </div>
        {tagSuggestions.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {tagSuggestions.map(t => (
              <button key={t} onClick={() => addTag(t)} style={{ fontSize: 12, padding: "3px 10px", borderRadius: 99, background: "var(--surface-hover)", color: "var(--ink-secondary)", border: "1px solid var(--line)", cursor: "pointer" }}>
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      <RichEditor html={initialHtml} onChange={html => onChange({ ...draft, body_md: html })} />
    </div>
  );
}
